import type { Messages } from "./en";

const zh: Messages = {
  meta: {
    siteTagline: "以生物基材料，构建面向循环的未来电子",
    homeDescription: "面向电子领域的生物基与可降解材料精选库，每个条目均链接至其来源文献，并收录使用这些材料的项目。",
    materialsTitle: "材料数据库",
    materialsDescription: "浏览面向电子领域的生物基与可降解材料，可按材料类别、降解数据、拉伸强度与来源筛选。每个条目均链接至其来源文献。",
    applicationsTitle: "应用案例",
    applicationsDescription: "在电子领域使用生物基与可降解材料的项目与原型，每个案例均附来源链接。",
    aboutTitle: "关于",
    aboutDescription: "BioNexus 是什么、条目如何整理与引用来源，以及如何参与贡献。",
    materialDescription: (name, family) => `${name}${family ? `（${family}）` : ""}：BioNexus 收录的实测属性与来源文献。`,
    applicationDescription: (title) => `${title}：BioNexus 收录的项目详情、所用材料与来源。`,
  },
  nav: {
    materials: "材料",
    applications: "应用",
    about: "关于",
    admin: "管理",
    light: "浅色",
    dark: "深色",
    auto: "自动",
    signOut: "退出",
    language: "语言",
  },
  common: {
    backHome: "返回首页",
    backMaterials: "返回材料列表",
    backApplications: "返回应用列表",
    viewAll: "查看全部",
    readCase: "阅读案例",
    lastUpdated: (date) => `最后更新：${date}`,
    image: "图片",
    source: "来源",
    method: "方法",
    general: "通用",
    onThisPage: "本页目录",
    notFound: "页面不存在",
    returnHome: "返回首页",
    contentLanguageNote: "条目内容以来源语言（英文）显示",
  },
  applicationTypes: {
    academic: "学术",
    commercial: "商业",
    "open-source": "开源",
    community: "社区",
  },
  home: {
    heroA: "面向",
    heroB: "循环的未来电子，",
    heroC: "以生物基材料构建",
    heroLead: "面向电子领域的生物基材料精选库。",
    exploreMaterials: "浏览材料",
    seeApplications: "查看应用",
    statMaterials: "材料",
    statApplications: "应用",
    statSources: "引用来源",
    latestApplications: "最新应用",
    featuredMaterials: "精选材料",
    noApplications: "暂无应用案例。",
    noMaterials: "暂无材料。",
  },
  loop: {
    stages: ["生长", "制造", "使用", "回归"],
    aria: "材料生命周期：生长、制造、使用、回归",
    footnote: "不同材料的废弃处理方式各不相同，请参阅各自来源。",
  },
  materials: {
    eyebrow: "材料数据库",
    title: "面向电子的生物基材料",
    lead: "每个条目均链接至其来源；仅显示来源中报告的数值。",
    allFamilies: "全部类别",
    filterLabel: "按数据完整度筛选材料",
    filters: {
      all: "全部数据",
      complete: "≥3 项实测属性",
      sourced: "有论文来源",
      degradation: "有降解数据",
      tensile: "有拉伸强度",
    },
    count: (n) => `${n} 种材料`,
    empty: "没有符合筛选条件的材料。",
    clearFilters: "清除筛选",
    measured: (n) => `${n} 项实测属性`,
    measuredTitle: "三项及以上实测属性，且均有引用来源",
  },
  applications: {
    featured: "精选",
    readTheCase: "阅读案例",
    all: "全部应用",
    count: (n) => `${n} 个案例`,
    emptyTitle: "暂无应用案例",
    emptyText: "敬请期待。",
  },
  material: {
    notFound: "未找到该材料",
    sections: {
      overview: "概述",
      properties: "实测属性",
      abstract: "摘要",
      fabrication: "制备方法",
      context: "研究背景",
      applications: "相关应用",
    },
    facts: { family: "类别", primaryUse: "主要用途", degradation: "生物降解", trl: "TRL · BioNexus 评估" },
    props: {
      degradation: "生物降解",
      tensile: "拉伸强度",
      electrical: "电学性能",
      water: "耐水性",
      cost: "成本",
      trl: "技术成熟度",
    },
    readFullAbstract: "展开完整摘要",
    showLess: "收起",
    comparedWith: "对比材料",
    referencedFigures: "引用图表",
    researchSource: "研究来源",
    viewPaper: "查看论文",
    applicationsFromPaper: "应用方向（据论文）",
    moreFromFamily: (family) => `更多 ${family} 类材料`,
    application: "应用",
  },
  application: {
    notFound: "未找到该应用",
    noDescription: "暂无描述。",
    sections: {
      overview: "概述",
      features: "主要特点",
      technology: "工作原理",
      impact: "可持续性影响",
      team: "项目团队",
      materials: "所用材料",
    },
    facts: { type: "类型", category: "类别", year: "年份", trl: "TRL · BioNexus 评估" },
    technologies: "相关技术",
    leadOrganization: "牵头机构",
    projectSource: "项目来源",
    tags: "标签",
    more: "更多应用",
  },
  trl: {
    stages: [
      "观察到基本原理",
      "形成技术概念",
      "实验性概念验证",
      "技术在实验室中得到验证",
      "技术在相关环境中得到验证（对关键使能技术而言为工业相关环境）",
      "技术在相关环境中得到演示（对关键使能技术而言为工业相关环境）",
      "系统原型在运行环境中演示",
      "系统完成并通过鉴定",
      "实际系统在运行环境中得到验证（对关键使能技术而言为具备竞争力的制造；或在太空中）",
    ],
    assessedBy: "由 BioNexus 依据以下标准评估：",
    scale: "欧盟 TRL 标准",
    translationNote: "（非官方译文）",
    dotsTitle: "TRL 由 BioNexus 评估",
    aria: (level, stage) => `技术成熟度 ${level}/9：${stage}（由 BioNexus 评估）`,
  },
  footer: {
    tagline: "面向电子的生物基材料",
    navigation: "导航",
    contact: "联系",
    legal: "法律信息",
    rights: "保留所有权利。",
    research: "内容仅供研究参考。",
    colophon: "版本说明与参考文献",
    data: "数据：实测数值取自每个条目所引用的来源；来源未报告的数值不予显示。",
    trlBefore: "技术成熟度（TRL）由 BioNexus 依据以下标准评估：",
    trlLink: "欧盟 Horizon 2020 TRL 标准（通用附件 G 部分）",
    images: "图片来自原始论文和项目页面，并在各条目页署名和链接。本站不使用 AI 生成的图片。",
    typefaces: "字体：",
    typefacesAfter: "均采用 SIL 开源字体许可，由 Google Fonts 提供。",
    icons: "图标：",
    iconsAfter: "（ISC 许可）。网站使用 React、Tailwind CSS、Radix UI 和 shadcn/ui 构建。",
    contrastBefore: "文字与界面颜色按照",
    contrastLink: "WCAG 2.1 AA 对比度标准",
    contrastAfter: "选取；当系统要求减少动态效果时，动画会相应减弱。",
    language: "界面提供英文、中文和意大利文。条目内容（描述、摘要、实测数值）以其来源语言显示，通常为英文。",
  },
  about: {
    title: "关于 BioNexus",
    whatTitle: "BioNexus 是什么？",
    whatText:
      "一个独立媒体平台，记录可降解材料及其在物联网中的应用。我们追踪再生电子领域的同行评审研究、真实项目与市场动态。",
    whyTitle: "为何重要",
    why1a: "2019 年全球产生 5360 万公吨电子垃圾",
    why1b: "。随着数十亿物联网传感器和一次性电子产品进入生态系统，可降解性变得至关重要。",
    why2a: "可降解电子聚合物市场规模：1.26 亿美元（2024）→ 2.46 亿美元（2033）",
    why2b: "。从医疗植入物到智慧农业，能够先发挥性能、再自行分解的材料正从研究走向现实。",
    source1: "联合国《2020 年全球电子垃圾监测报告》",
    source1After: "，ITU/UNITAR/ISWA",
    source2: "Grand View Research",
    source2After: "，Biodegradable Electronics Polymers Market，2024 年 10 月",
    approachTitle: "我们的方法",
    approachText: "聚焦可靠来源，对可降解电子研究进行人工整理。",
    approachList: ["优先收录同行评审文献", "人工审核与核实", "所有数据点均注明来源", "聚焦可降解电子应用"],
    approachNote: "这并非一个全面的数据库，而是对相关材料与项目的精选。",
    resourcesTitle: "相关资源",
    databases: "材料数据库",
    fmb: "材料与作品的共享平台",
    materiom: "开源生物材料配方",
    contributeTitle: "参与贡献",
    contributeText: "发现了相关论文？正在开发可降解设备？",
    submit: "提交文献",
  },
};

export default zh;
